/**
 * Dependency-free QR encoder for the share modal's "scan to open" code.
 * Byte mode, error-correction level M, versions 1–6 (up to 106 bytes — far
 * beyond any share URL). Pure: text in, boolean module matrix out; the
 * rendering (SVG, token colors) lives in QrCode.tsx. Correctness is pinned by
 * qr.test.ts, which round-trips the matrix through a real decoder (jsqr).
 */

/* ---------------------------------------------------- GF(256) arithmetic */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a]! + LOG[b]!]!;
}

/** Reed–Solomon generator polynomial of the given degree (highest term first). */
function rsGenerator(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j++) {
      next[j]! ^= poly[j]!; // × x
      next[j + 1]! ^= gfMul(poly[j]!, EXP[i]!); // × α^i
    }
    poly = next;
  }
  return poly;
}

/** RS error-correction codewords for one data block. */
function rsEncode(data: number[], eccLen: number): number[] {
  const gen = rsGenerator(eccLen);
  const res = new Array<number>(eccLen).fill(0);
  for (const byte of data) {
    const factor = byte ^ res[0]!;
    res.shift();
    res.push(0);
    if (factor !== 0) {
      for (let j = 0; j < eccLen; j++) res[j]! ^= gfMul(gen[j + 1]!, factor);
    }
  }
  return res;
}

/* -------------------------------------------------------- version tables */

interface VersionSpec {
  version: number;
  /** Alignment-pattern center coordinates. */
  align: number[];
  /** Number of (equal-size) data blocks. */
  blocks: number;
  /** Data codewords per block. */
  dataPerBlock: number;
  /** ECC codewords per block. */
  eccPerBlock: number;
}

/** ECC level M block structure, versions 1–6 (ISO/IEC 18004 table 9). */
const VERSIONS: VersionSpec[] = [
  { version: 1, align: [], blocks: 1, dataPerBlock: 16, eccPerBlock: 10 },
  { version: 2, align: [6, 18], blocks: 1, dataPerBlock: 28, eccPerBlock: 16 },
  { version: 3, align: [6, 22], blocks: 1, dataPerBlock: 44, eccPerBlock: 26 },
  { version: 4, align: [6, 26], blocks: 2, dataPerBlock: 32, eccPerBlock: 18 },
  { version: 5, align: [6, 30], blocks: 2, dataPerBlock: 43, eccPerBlock: 24 },
  { version: 6, align: [6, 34], blocks: 4, dataPerBlock: 27, eccPerBlock: 16 },
];

/* --------------------------------------------------------- bit assembly */

class BitBuffer {
  bits: number[] = [];

  push(value: number, length: number) {
    for (let i = length - 1; i >= 0; i--) this.bits.push((value >> i) & 1);
  }
}

/** Byte-mode segment → the padded data codeword stream for a version. */
function dataCodewords(bytes: Uint8Array, spec: VersionSpec): number[] {
  const capacity = spec.blocks * spec.dataPerBlock;
  const buf = new BitBuffer();
  buf.push(0b0100, 4); // byte mode
  buf.push(bytes.length, 8); // count indicator (8 bits for versions 1–9)
  for (const b of bytes) buf.push(b, 8);

  const capacityBits = capacity * 8;
  buf.push(0, Math.min(4, capacityBits - buf.bits.length)); // terminator
  while (buf.bits.length % 8 !== 0) buf.bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < buf.bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | buf.bits[i + j]!;
    codewords.push(byte);
  }
  const pads = [0xec, 0x11];
  for (let i = 0; codewords.length < capacity; i++) codewords.push(pads[i % 2]!);
  return codewords;
}

/** Split into blocks, append RS ecc, interleave (equal blocks at M ≤ v6). */
function finalCodewords(data: number[], spec: VersionSpec): number[] {
  const blocks: number[][] = [];
  const eccBlocks: number[][] = [];
  for (let b = 0; b < spec.blocks; b++) {
    const block = data.slice(b * spec.dataPerBlock, (b + 1) * spec.dataPerBlock);
    blocks.push(block);
    eccBlocks.push(rsEncode(block, spec.eccPerBlock));
  }
  const out: number[] = [];
  for (let i = 0; i < spec.dataPerBlock; i++) for (const block of blocks) out.push(block[i]!);
  for (let i = 0; i < spec.eccPerBlock; i++) for (const block of eccBlocks) out.push(block[i]!);
  return out;
}

/* -------------------------------------------------------- matrix layout */

type Matrix = (boolean | null)[][];

function placeFinder(m: Matrix, fn: boolean[][], top: number, left: number) {
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const rr = top + r;
      const cc = left + c;
      if (rr < 0 || cc < 0 || rr >= m.length || cc >= m.length) continue;
      const edge = r >= 0 && r <= 6 && c >= 0 && c <= 6 && (r === 0 || r === 6 || c === 0 || c === 6);
      const core = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      m[rr]![cc] = edge || core;
      fn[rr]![cc] = true;
    }
  }
}

function placeAlignment(m: Matrix, fn: boolean[][], row: number, col: number) {
  for (let r = -2; r <= 2; r++) {
    for (let c = -2; c <= 2; c++) {
      m[row + r]![col + c] = Math.max(Math.abs(r), Math.abs(c)) !== 1;
      fn[row + r]![col + c] = true;
    }
  }
}

const MASKS: Array<(r: number, c: number) => boolean> = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

/** BCH(15,5)-protected format info bits for (ECC M, mask). MSB first. */
function formatBits(mask: number): number[] {
  const data = (0b00 << 3) | mask; // ECC level M = 00
  let value = data << 10;
  for (let i = 14; i >= 10; i--) {
    if ((value >> i) & 1) value ^= 0b10100110111 << (i - 10);
  }
  const combined = ((data << 10) | value) ^ 0b101010000010010;
  const bits: number[] = [];
  for (let i = 14; i >= 0; i--) bits.push((combined >> i) & 1);
  return bits;
}

function placeFormat(m: Matrix, mask: number) {
  const size = m.length;
  const bits = formatBits(mask);
  // Copy 1 — around the top-left finder.
  const copy1: Array<[number, number]> = [];
  for (let c = 0; c <= 5; c++) copy1.push([8, c]);
  copy1.push([8, 7], [8, 8], [7, 8]);
  for (let r = 5; r >= 0; r--) copy1.push([r, 8]);
  // Copy 2 — split between the bottom-left and top-right finders.
  const copy2: Array<[number, number]> = [];
  for (let r = size - 1; r >= size - 7; r--) copy2.push([r, 8]);
  for (let c = size - 8; c <= size - 1; c++) copy2.push([8, c]);
  bits.forEach((bit, i) => {
    const [r1, c1] = copy1[i]!;
    const [r2, c2] = copy2[i]!;
    m[r1]![c1] = bit === 1;
    m[r2]![c2] = bit === 1;
  });
}

/** Standard mask penalty (ISO 18004 §8.8.2) — pick the calmest pattern. */
function penalty(m: Matrix): number {
  const size = m.length;
  const at = (r: number, c: number) => (m[r]![c] === true ? 1 : 0);
  let score = 0;

  // N1 — runs of ≥5 same-color modules in a row/column.
  for (let axis = 0; axis < 2; axis++) {
    for (let i = 0; i < size; i++) {
      let run = 1;
      for (let j = 1; j < size; j++) {
        const cur = axis === 0 ? at(i, j) : at(j, i);
        const prev = axis === 0 ? at(i, j - 1) : at(j - 1, i);
        if (cur === prev) {
          run++;
          if (j === size - 1 && run >= 5) score += 3 + (run - 5);
        } else {
          if (run >= 5) score += 3 + (run - 5);
          run = 1;
        }
      }
    }
  }

  // N2 — 2×2 blocks of one color.
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const v = at(r, c);
      if (v === at(r, c + 1) && v === at(r + 1, c) && v === at(r + 1, c + 1)) score += 3;
    }
  }

  // N3 — finder-like 1:1:3:1:1 pattern with 4 light modules on either side.
  const needle = [1, 0, 1, 1, 1, 0, 1];
  const clear = [0, 0, 0, 0];
  for (let axis = 0; axis < 2; axis++) {
    for (let i = 0; i < size; i++) {
      for (let j = 0; j <= size - 7; j++) {
        let hit = true;
        for (let k = 0; k < 7; k++) {
          if ((axis === 0 ? at(i, j + k) : at(j + k, i)) !== needle[k]) {
            hit = false;
            break;
          }
        }
        if (!hit) continue;
        const side = (start: number) => {
          if (start < 0 || start + 4 > size) return false;
          for (let k = 0; k < 4; k++) {
            if ((axis === 0 ? at(i, start + k) : at(start + k, i)) !== clear[k]) return false;
          }
          return true;
        };
        if (side(j - 4) || side(j + 7)) score += 40;
      }
    }
  }

  // N4 — dark-module proportion deviation from 50%.
  let dark = 0;
  for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) dark += at(r, c);
  score += Math.floor(Math.abs((dark * 100) / (size * size) - 50) / 5) * 10;
  return score;
}

/* --------------------------------------------------------------- encode */

/**
 * Encode text as a QR matrix (true = dark). ECC level M, byte mode; throws
 * if the payload exceeds version 6 (106 bytes) — share URLs never do.
 */
export function qrMatrix(text: string): boolean[][] {
  const bytes = new TextEncoder().encode(text);
  const spec = VERSIONS.find((v) => v.blocks * v.dataPerBlock - 2 >= bytes.length);
  if (!spec) throw new Error(`QR payload too long (${bytes.length} bytes; max 106)`);

  const size = 17 + 4 * spec.version;
  const matrix: Matrix = Array.from({ length: size }, () => Array<boolean | null>(size).fill(null));
  const isFunction: boolean[][] = Array.from({ length: size }, () => Array<boolean>(size).fill(false));

  // Function patterns: finders, timing, alignment, dark module.
  placeFinder(matrix, isFunction, 0, 0);
  placeFinder(matrix, isFunction, 0, size - 7);
  placeFinder(matrix, isFunction, size - 7, 0);
  for (let i = 8; i < size - 8; i++) {
    matrix[6]![i] = i % 2 === 0;
    matrix[i]![6] = i % 2 === 0;
    isFunction[6]![i] = true;
    isFunction[i]![6] = true;
  }
  for (const row of spec.align) {
    for (const col of spec.align) {
      if (isFunction[row]![col]) continue; // overlaps a finder
      placeAlignment(matrix, isFunction, row, col);
    }
  }
  matrix[size - 8]![8] = true;
  isFunction[size - 8]![8] = true;

  // Reserve the format-info areas so data placement skips them.
  for (let i = 0; i <= 8; i++) {
    if (i !== 6) {
      isFunction[8]![i] = true;
      isFunction[i]![8] = true;
    }
    if (i < 8) {
      isFunction[8]![size - 1 - i] = true;
      isFunction[size - 1 - i]![8] = true;
    }
  }

  // Codeword bit stream, MSB first (trailing remainder modules stay 0).
  const codewords = finalCodewords(dataCodewords(bytes, spec), spec);
  const bits: number[] = [];
  for (const cw of codewords) for (let i = 7; i >= 0; i--) bits.push((cw >> i) & 1);

  // Zigzag placement: column pairs right → left, skipping the timing column.
  let bitIndex = 0;
  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let step = 0; step < size; step++) {
      const row = upward ? size - 1 - step : step;
      for (const col of [right, right - 1]) {
        if (isFunction[row]![col]) continue;
        matrix[row]![col] = (bits[bitIndex] ?? 0) === 1;
        bitIndex++;
      }
    }
    upward = !upward;
  }

  // Try every mask; keep the lowest penalty.
  let best: boolean[][] | null = null;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask++) {
    const candidate: Matrix = matrix.map((row) => [...row]);
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (!isFunction[r]![c] && MASKS[mask]!(r, c)) candidate[r]![c] = !candidate[r]![c];
      }
    }
    placeFormat(candidate, mask);
    const score = penalty(candidate);
    if (score < bestScore) {
      bestScore = score;
      best = candidate.map((row) => row.map((v) => v === true));
    }
  }
  return best!;
}
